import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

/**
 * Generates a comprehensive clinical recommendation PDF report with physician details
 * @param {Object} recommendation - The AI recommendation object
 * @param {Object} patientInfo - Patient demographic and clinical information
 * @param {string} selectedRegion - Selected body region
 * @param {Object} physicianInfo - Referring physician information from user profile
 * @returns {Promise<void>}
 */
export const generateRecommendationPDF = async (recommendation, patientInfo, selectedRegion, physicianInfo) => {
  try {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf?.internal?.pageSize?.getWidth();
    const pageHeight = pdf?.internal?.pageSize?.getHeight();
    const margin = 15;
    let yPosition = margin;

    // Helper function to add text with word wrapping
    const addWrappedText = (text, x, y, maxWidth, lineHeight = 6) => {
      const lines = pdf?.splitTextToSize(text, maxWidth);
      pdf?.text(lines, x, y);
      return lines?.length * lineHeight;
    };

    // Helper function to check page overflow and add new page if needed
    const checkPageOverflow = (requiredSpace) => {
      if (yPosition + requiredSpace > pageHeight - margin) {
        pdf?.addPage();
        yPosition = margin;
        return true;
      }
      return false;
    };

    // === Header Section ===
    pdf?.setFillColor(59, 130, 246);
    pdf?.rect(0, 0, pageWidth, 40, 'F');
    
    pdf?.setTextColor(255, 255, 255);
    pdf?.setFontSize(24);
    pdf?.setFont('helvetica', 'bold');
    pdf?.text('Clinical Imaging Recommendation', margin, 18);
    
    pdf?.setFontSize(10);
    pdf?.setFont('helvetica', 'normal');
    pdf?.text(`Generated: ${new Date()?.toLocaleString()}`, margin, 28);
    pdf?.text(`Report ID: ${recommendation?.id || 'N/A'}`, margin, 34);
    
    yPosition = 50;

    // === Referring Physician Information Section ===
    pdf?.setTextColor(0, 0, 0);
    pdf?.setFillColor(243, 244, 246);
    pdf?.rect(margin, yPosition, pageWidth - (2 * margin), 8, 'F');
    
    pdf?.setFontSize(14);
    pdf?.setFont('helvetica', 'bold');
    pdf?.text('Referring Physician Information', margin + 3, yPosition + 6);
    yPosition += 12;

    pdf?.setFontSize(10);
    pdf?.setFont('helvetica', 'normal');
    const physicianDetails = [
      `Physician Name: ${physicianInfo?.full_name || 'N/A'}`,
      `License Number: ${physicianInfo?.license_number || 'N/A'}`,
      `Specialization: ${physicianInfo?.specialization || 'N/A'}`,
      `Institution: ${physicianInfo?.institution_name || 'N/A'}`,
      `Institution Country: ${physicianInfo?.institution_country || 'N/A'}`,
      `Date of Recommendation: ${new Date()?.toLocaleDateString()}`,
      `Years of Practice: ${physicianInfo?.years_of_practice ? physicianInfo?.years_of_practice + ' years' : 'N/A'}`,
      `Contact: ${physicianInfo?.email || 'N/A'}`
    ];

    physicianDetails?.forEach((detail, index) => {
      if (index % 2 === 0) {
        pdf?.text(detail, margin + 5, yPosition);
      } else {
        pdf?.text(detail, pageWidth / 2 + 5, yPosition);
        yPosition += 6;
      }
    });
    
    yPosition += 8;

    // === Patient Demographics Section ===
    checkPageOverflow(40);
    pdf?.setFillColor(243, 244, 246);
    pdf?.rect(margin, yPosition, pageWidth - (2 * margin), 8, 'F');
    
    pdf?.setFontSize(14);
    pdf?.setFont('helvetica', 'bold');
    pdf?.text('Patient Demographics', margin + 3, yPosition + 6);
    yPosition += 12;

    pdf?.setFontSize(10);
    pdf?.setFont('helvetica', 'normal');
    const demographics = [
      `Patient ID: ${patientInfo?.patientId || 'N/A'}`,
      `Age: ${patientInfo?.patientAge || 'N/A'} years`,
      `Gender: ${patientInfo?.gender || 'N/A'}`,
      `Weight: ${patientInfo?.weight ? patientInfo?.weight + ' kg' : 'N/A'}`,
      `Emergency Case: ${patientInfo?.isEmergency ? 'Yes' : 'No'}`,
      `Pregnancy Status: ${patientInfo?.isPregnant ? 'Yes' : 'N/A'}`
    ];

    demographics?.forEach((item, index) => {
      if (index % 2 === 0) {
        pdf?.text(item, margin + 5, yPosition);
      } else {
        pdf?.text(item, pageWidth / 2 + 5, yPosition);
        yPosition += 6;
      }
    });
    
    yPosition += 8;

    // === Clinical Presentation Section ===
    checkPageOverflow(40);
    
    pdf?.setFillColor(243, 244, 246);
    pdf?.rect(margin, yPosition, pageWidth - (2 * margin), 8, 'F');
    
    pdf?.setFontSize(14);
    pdf?.setFont('helvetica', 'bold');
    pdf?.text('Clinical Presentation', margin + 3, yPosition + 6);
    yPosition += 12;

    pdf?.setFontSize(10);
    pdf?.setFont('helvetica', 'bold');
    pdf?.text('Chief Complaint:', margin + 5, yPosition);
    yPosition += 6;
    
    pdf?.setFont('helvetica', 'normal');
    const complaintHeight = addWrappedText(
      patientInfo?.chiefComplaint || 'N/A',
      margin + 5,
      yPosition,
      pageWidth - (2 * margin) - 10
    );
    yPosition += complaintHeight + 4;

    pdf?.setFont('helvetica', 'bold');
    pdf?.text('Symptom Duration:', margin + 5, yPosition);
    pdf?.setFont('helvetica', 'normal');
    pdf?.text(patientInfo?.symptomDuration || 'N/A', margin + 45, yPosition);
    yPosition += 6;

    pdf?.setFont('helvetica', 'bold');
    pdf?.text('Severity:', margin + 5, yPosition);
    pdf?.setFont('helvetica', 'normal');
    pdf?.text(patientInfo?.severity || 'N/A', margin + 25, yPosition);
    yPosition += 10;

    pdf?.setFont('helvetica', 'bold');
    pdf?.text('Clinical History:', margin + 5, yPosition);
    yPosition += 6;
    
    pdf?.setFont('helvetica', 'normal');
    const historyHeight = addWrappedText(
      patientInfo?.clinicalHistory || 'N/A',
      margin + 5,
      yPosition,
      pageWidth - (2 * margin) - 10
    );
    yPosition += historyHeight + 4;

    if (patientInfo?.examinationFindings) {
      checkPageOverflow(30);
      pdf?.setFont('helvetica', 'bold');
      pdf?.text('Examination Findings:', margin + 5, yPosition);
      yPosition += 6;
      
      pdf?.setFont('helvetica', 'normal');
      const findingsHeight = addWrappedText(
        patientInfo?.examinationFindings,
        margin + 5,
        yPosition,
        pageWidth - (2 * margin) - 10
      );
      yPosition += findingsHeight + 4;
    }

    yPosition += 6;

    // === Imaging Request Details ===
    checkPageOverflow(30);
    
    pdf?.setFillColor(243, 244, 246);
    pdf?.rect(margin, yPosition, pageWidth - (2 * margin), 8, 'F');
    
    pdf?.setFontSize(14);
    pdf?.setFont('helvetica', 'bold');
    pdf?.text('Imaging Request Details', margin + 3, yPosition + 6);
    yPosition += 12;

    pdf?.setFontSize(10);
    pdf?.setFont('helvetica', 'bold');
    pdf?.text('Body Region:', margin + 5, yPosition);
    pdf?.setFont('helvetica', 'normal');
    pdf?.text(selectedRegion || 'Not specified', margin + 10, yPosition);
    yPosition += 10;

    yPosition += 8;

    // === AI Recommendation Section ===
    checkPageOverflow(50);
    
    pdf?.setFillColor(219, 234, 254);
    pdf?.rect(margin, yPosition, pageWidth - (2 * margin), 8, 'F');
    
    pdf?.setFontSize(14);
    pdf?.setFont('helvetica', 'bold');
    pdf?.setTextColor(37, 99, 235);
    pdf?.text('🤖 AI-Generated Recommendation', margin + 3, yPosition + 6);
    yPosition += 12;

    // Evidence Grade Badge
    pdf?.setTextColor(0, 0, 0);
    pdf?.setFontSize(10);
    pdf?.setFont('helvetica', 'bold');
    
    if (recommendation?.evidenceGrade) {
      const gradeColors = {
        'A': [34, 197, 94],
        'B': [251, 146, 60],
        'C': [239, 68, 68]
      };
      const color = gradeColors?.[recommendation?.evidenceGrade] || [156, 163, 175];
      
      pdf?.setFillColor(...color);
      pdf?.setTextColor(255, 255, 255);
      pdf?.roundedRect(margin + 5, yPosition - 3, 30, 8, 2, 2, 'F');
      pdf?.text(`Grade ${recommendation?.evidenceGrade}`, margin + 10, yPosition + 3);
      
      pdf?.setTextColor(0, 0, 0);
      pdf?.setFont('helvetica', 'normal');
      pdf?.text(`Confidence: ${Math.round((recommendation?.confidenceScore || 0.85) * 100)}%`, margin + 45, yPosition + 3);
      yPosition += 12;
    }

    // Primary Recommendation
    pdf?.setFontSize(12);
    pdf?.setFont('helvetica', 'bold');
    pdf?.text('Recommended Imaging:', margin + 5, yPosition);
    yPosition += 7;
    
    pdf?.setFontSize(14);
    pdf?.setTextColor(37, 99, 235);
    pdf?.text(recommendation?.primaryModality || recommendation?.recommendedModality || 'N/A', margin + 5, yPosition);
    yPosition += 10;

    // Modality Rationale
    pdf?.setFontSize(10);
    pdf?.setFont('helvetica', 'normal');
    pdf?.setTextColor(0, 0, 0);
    if (recommendation?.modalityRationale) {
      const rationaleHeight = addWrappedText(
        recommendation?.modalityRationale,
        margin + 5,
        yPosition,
        pageWidth - (2 * margin) - 10
      );
      yPosition += rationaleHeight + 6;
    }

    // Clinical Rationale
    checkPageOverflow(40);
    pdf?.setFont('helvetica', 'bold');
    pdf?.text('Clinical Rationale:', margin + 5, yPosition);
    yPosition += 6;
    
    pdf?.setFont('helvetica', 'normal');
    const clinicalRationaleText = recommendation?.clinicalRationale || recommendation?.reasoning || 'N/A';
    const rationaleHeight = addWrappedText(
      clinicalRationaleText,
      margin + 5,
      yPosition,
      pageWidth - (2 * margin) - 10
    );
    yPosition += rationaleHeight + 8;

    // Urgency and Radiation Info
    pdf?.setFont('helvetica', 'bold');
    pdf?.text('Urgency:', margin + 5, yPosition);
    pdf?.setFont('helvetica', 'normal');
    pdf?.text((recommendation?.urgency || 'Routine')?.toUpperCase(), margin + 25, yPosition);
    yPosition += 6;

    if (recommendation?.radiationDose) {
      pdf?.setFont('helvetica', 'bold');
      pdf?.text('Radiation Dose:', margin + 5, yPosition);
      pdf?.setFont('helvetica', 'normal');
      pdf?.text(`${recommendation?.radiationDose} mSv`, margin + 35, yPosition);
      yPosition += 6;
    }

    yPosition += 6;

    // iRefer Guidelines Section
    if (recommendation?.ireferCode) {
      checkPageOverflow(40);
      
      pdf?.setFillColor(243, 244, 246);
      pdf?.rect(margin, yPosition, pageWidth - (2 * margin), 8, 'F');
      
      pdf?.setFontSize(14);
      pdf?.setFont('helvetica', 'bold');
      pdf?.setTextColor(0, 0, 0);
      pdf?.text('iRefer Guidelines', margin + 3, yPosition + 6);
      yPosition += 12;

      pdf?.setFontSize(10);
      pdf?.setFont('helvetica', 'bold');
      pdf?.text('iRefer Code:', margin + 5, yPosition);
      pdf?.setFont('helvetica', 'normal');
      pdf?.text(recommendation?.ireferCode, margin + 30, yPosition);
      yPosition += 6;

      if (recommendation?.ireferUrl) {
        pdf?.setFont('helvetica', 'bold');
        pdf?.text('Reference:', margin + 5, yPosition);
        pdf?.setFont('helvetica', 'normal');
        pdf?.setTextColor(37, 99, 235);
        pdf?.text(recommendation?.ireferUrl, margin + 25, yPosition);
        pdf?.setTextColor(0, 0, 0);
        yPosition += 6;
      }

      if (recommendation?.ireferConfidence) {
        pdf?.setFont('helvetica', 'bold');
        pdf?.text('Match Confidence:', margin + 5, yPosition);
        pdf?.setFont('helvetica', 'normal');
        pdf?.text(`${Math.round(recommendation?.ireferConfidence * 100)}%`, margin + 40, yPosition);
        yPosition += 6;
      }

      yPosition += 8;
    }

    // Alternative Modalities
    if (recommendation?.alternativeModalities && recommendation?.alternativeModalities?.length > 0) {
      checkPageOverflow(40);
      
      pdf?.setFillColor(243, 244, 246);
      pdf?.rect(margin, yPosition, pageWidth - (2 * margin), 8, 'F');
      
      pdf?.setFontSize(14);
      pdf?.setFont('helvetica', 'bold');
      pdf?.text('Alternative Options', margin + 3, yPosition + 6);
      yPosition += 12;

      pdf?.setFontSize(10);
      recommendation?.alternativeModalities?.forEach((alt, index) => {
        checkPageOverflow(20);
        
        pdf?.setFont('helvetica', 'bold');
        pdf?.text(`${index + 1}. ${alt?.modality || alt}`, margin + 5, yPosition);
        yPosition += 6;
        
        if (alt?.rationale) {
          pdf?.setFont('helvetica', 'normal');
          const altRationaleHeight = addWrappedText(
            alt?.rationale,
            margin + 10,
            yPosition,
            pageWidth - (2 * margin) - 15
          );
          yPosition += altRationaleHeight + 4;
        }
      });
      
      yPosition += 6;
    }

    // Safety Warnings
    if (recommendation?.safetyWarnings && recommendation?.safetyWarnings?.length > 0) {
      checkPageOverflow(40);
      
      pdf?.setFillColor(254, 226, 226);
      pdf?.rect(margin, yPosition - 2, pageWidth - (2 * margin), 10 + (recommendation?.safetyWarnings?.length * 6), 'F');
      
      pdf?.setFont('helvetica', 'bold');
      pdf?.setTextColor(220, 38, 38);
      pdf?.text('⚠️ Safety Considerations:', margin + 5, yPosition + 4);
      yPosition += 10;
      
      pdf?.setTextColor(0, 0, 0);
      pdf?.setFont('helvetica', 'normal');
      recommendation?.safetyWarnings?.forEach((warning) => {
        pdf?.text(`• ${warning}`, margin + 8, yPosition);
        yPosition += 6;
      });
      yPosition += 6;
    }

    // Evidence & Guidelines
    if (recommendation?.iReferGuidelines && recommendation?.iReferGuidelines?.length > 0) {
      checkPageOverflow(50);
      
      pdf?.setTextColor(0, 0, 0);
      pdf?.setFillColor(243, 244, 246);
      pdf?.rect(margin, yPosition, pageWidth - (2 * margin), 8, 'F');
      
      pdf?.setFontSize(12);
      pdf?.setFont('helvetica', 'bold');
      pdf?.text('Evidence & Guidelines', margin + 3, yPosition + 6);
      yPosition += 12;
      
      pdf?.setFontSize(9);
      recommendation?.iReferGuidelines?.forEach((guideline, index) => {
        checkPageOverflow(20);
        pdf?.setFont('helvetica', 'bold');
        
        // Handle both string and object formats
        const guidelineText = typeof guideline === 'string' ? guideline : guideline?.title;
        pdf?.text(`${index + 1}. ${guidelineText}`, margin + 5, yPosition);
        yPosition += 5;
        
        // Only show reference and link if guideline is an object
        if (typeof guideline === 'object' && guideline !== null) {
          pdf?.setFont('helvetica', 'normal');
          if (guideline?.reference) {
            pdf?.text(`Reference: ${guideline?.reference}`, margin + 8, yPosition);
            yPosition += 5;
          }
          
          if (guideline?.link && typeof guideline?.link === 'string') {
            pdf?.setTextColor(37, 99, 235);
            pdf?.text(guideline?.link, margin + 8, yPosition);
            pdf?.setTextColor(0, 0, 0);
            yPosition += 8;
          }
        } else {
          yPosition += 3;
        }
      });
    }

    // === Professional Approval Section ===
    checkPageOverflow(40);
    
    pdf?.setFillColor(240, 253, 244);
    pdf?.rect(margin, yPosition, pageWidth - (2 * margin), 28, 'F');
    
    pdf?.setFontSize(11);
    pdf?.setFont('helvetica', 'bold');
    pdf?.setTextColor(34, 197, 94);
    pdf?.text('✓ Medical Professional Approval', margin + 5, yPosition + 6);
    yPosition += 12;
    
    pdf?.setFontSize(9);
    pdf?.setFont('helvetica', 'normal');
    pdf?.setTextColor(0, 0, 0);
    const approvalText = `This clinical recommendation has been reviewed and approved by ${physicianInfo?.full_name || 'a qualified medical professional'} (License: ${physicianInfo?.license_number || 'N/A'}), a ${physicianInfo?.specialization || 'medical'} specialist at ${physicianInfo?.institution_name || 'the referring institution'}. This recommendation is based on evidence-based guidelines and current clinical best practices.`;
    
    const approvalHeight = addWrappedText(
      approvalText,
      margin + 5,
      yPosition,
      pageWidth - (2 * margin) - 10,
      5
    );
    yPosition += approvalHeight + 10;

    // === Footer Section ===
    const finalPage = pdf?.internal?.getNumberOfPages();
    for (let i = 1; i <= finalPage; i++) {
      pdf?.setPage(i);
      pdf?.setFontSize(8);
      pdf?.setFont('helvetica', 'normal');
      pdf?.setTextColor(128, 128, 128);
      
      pdf?.text(
        `Page ${i} of ${finalPage}`,
        pageWidth / 2,
        pageHeight - 10,
        { align: 'center' }
      );
      
      pdf?.text(
        'This recommendation is AI-generated and has been reviewed by a qualified healthcare professional.',
        pageWidth / 2,
        pageHeight - 6,
        { align: 'center' }
      );
    }

    // Save PDF
    const fileName = `clinical_recommendation_${patientInfo?.patientId || 'patient'}_${new Date()?.toISOString()?.split('T')?.[0]}.pdf`;
    pdf?.save(fileName);

    return {
      success: true,
      fileName: fileName
    };
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw error;
  }
};

/**
 * Generates a PDF from a DOM element using html2canvas
 * @param {HTMLElement} element - The DOM element to convert
 * @param {string} fileName - Name for the generated PDF file
 * @returns {Promise<void>}
 */
export const generatePDFFromElement = async (element, fileName = 'report.pdf') => {
  try {
    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff'
    });

    const imgWidth = 210;
    const imgHeight = (canvas?.height * imgWidth) / canvas?.width;
    
    const pdf = new jsPDF('p', 'mm', 'a4');
    const imgData = canvas?.toDataURL('image/png');
    
    pdf?.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
    pdf?.save(fileName);

    return { success: true, fileName };
  } catch (error) {
    console.error('Error generating PDF from element:', error);
    throw new Error('Failed to generate PDF from element. Please try again.');
  }
};

export default { generateRecommendationPDF, generatePDFFromElement };